import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PencilIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { DataRow, DataTable, RowActions, TableCell, TableHead } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { EmptyState } from '../shell/EmptyState.js';
import { IconButton } from '../components/IconButton.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useAuth } from '../auth/useAuth.js';
import { DomainForm } from './DomainForm.js';
import {
  useCreateDomain,
  useDeleteDomain,
  useGroup,
  useUpdateDomain,
  type Domain,
} from './queries.js';

export function GroupScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const groupId = Number(params.groupId ?? params.id);
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  const group = useGroup(groupId);
  const createDomain = useCreateDomain(groupId);
  const updateDomain = useUpdateDomain(groupId);
  const deleteDomain = useDeleteDomain(groupId);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Domain | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Domain | null>(null);

  return (
    <section aria-labelledby="group-heading" className="flex flex-col gap-(--space-5)">
      <header className="flex flex-wrap items-end justify-between gap-(--space-4)">
        <div>
          <Link
            to="/groups"
            className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle underline-offset-4 hover:underline"
          >
            ← Groups
          </Link>
          <h1
            id="group-heading"
            className="mt-(--space-1) text-[length:var(--text-display)] font-bold"
          >
            {group.data?.group.name ?? '…'}
          </h1>
          {group.data !== undefined && group.data.group.notes !== '' && (
            <p className="mt-(--space-2) max-w-[60ch] text-[length:var(--text-small)] font-medium text-ink-muted">
              {group.data.group.notes}
            </p>
          )}
        </div>
        {canEdit && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add domain
          </Button>
        )}
      </header>

      {group.isError && (
        <Callout tone="danger">
          {group.error instanceof Error ? group.error.message : 'Could not load this group'}
        </Callout>
      )}

      {group.data?.domains.length === 0 && (
        <EmptyState
          title="No domains yet"
          description="Add the hostname this client will point their traffic at. Until DNS reaches us, the Domains screen will show it as never seen."
          action={
            canEdit ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add the first domain
              </Button>
            ) : undefined
          }
        />
      )}

      {group.data !== undefined && group.data.domains.length > 0 && (
        <DataTable
          caption={`Domains in ${group.data.group.name}`}
          head={
            <>
              <TableHead scope="col">Hostname</TableHead>
              <TableHead scope="col">Fallback</TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col" className="text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </>
          }
        >
          {group.data.domains.map((domain) => (
            <DataRow
              key={domain.id}
              onActivate={() => void navigate(`/domains/${domain.id}`)}
              activateLabel={`Open domain ${domain.hostname}`}
            >
              <TableCell className="py-(--space-3) font-mono text-[length:var(--text-small)] font-bold text-ink">
                {domain.hostname}
              </TableCell>
              <TableCell className="py-(--space-3) font-mono text-[length:var(--text-small)] text-ink-muted">
                {domain.fallbackUrl ?? <span className="font-sans text-ink-subtle">global</span>}
              </TableCell>
              <TableCell className="py-(--space-3)">
                {/* Enabled/Disabled to match DomainForm's "Enabled" checkbox. */}
                <StatusBadge
                  status={domain.enabled ? 'active' : 'paused'}
                  label={domain.enabled ? 'Enabled' : 'Disabled'}
                />
              </TableCell>
              <TableCell className="py-(--space-3)">
                {canEdit && (
                  <RowActions>
                    {/* The only way to change a per-domain fallback URL after
                        creation, and the only way to take a client's traffic
                        offline without deleting the configuration. */}
                    <IconButton label={`Edit ${domain.hostname}`} onClick={() => setEditing(domain)}>
                      <PencilIcon aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label={`Delete ${domain.hostname}`}
                      side="left"
                      className="text-danger hover:bg-(--color-danger-quiet) hover:text-danger"
                      onClick={() => setPendingDelete(domain)}
                    >
                      <Trash2Icon aria-hidden="true" />
                    </IconButton>
                  </RowActions>
                )}
              </TableCell>
            </DataRow>
          ))}
        </DataTable>
      )}

      <Dialog open={adding} title="Add domain" onClose={() => setAdding(false)}>
        <DomainForm
          submitLabel="Add domain"
          onCancel={() => setAdding(false)}
          onSubmit={async (input) => {
            await createDomain.mutateAsync(input);
            setAdding(false);
          }}
        />
      </Dialog>

      <Dialog open={editing !== null} title="Edit domain" onClose={() => setEditing(null)}>
        {editing !== null && (
          <DomainForm
            initial={{
              hostname: editing.hostname,
              fallbackUrl: editing.fallbackUrl,
              enabled: editing.enabled,
            }}
            submitLabel="Save domain"
            onCancel={() => setEditing(null)}
            onSubmit={async (input) => {
              await updateDomain.mutateAsync({ id: editing.id, input });
              setEditing(null);
            }}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete domain"
        body={`Delete ${pendingDelete?.hostname ?? ''} and every path configured under it? Traffic arriving on this hostname will fall through to the global fallback. This cannot be undone.`}
        confirmLabel="Delete domain"
        busy={deleteDomain.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (pendingDelete === null) return;
          await deleteDomain.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </section>
  );
}
